/**
 * Hook personalizado para gerenciamento de autenticação
 * Encapsula lógica de autenticação e fornece métodos e estado para componentes
 */
import { useState, useEffect, useCallback } from 'react';
import { login, logout, getUserData, resetPassword, getJwt } from '../services/AuthService';
import { authEvents, refreshToken } from '../services/AxiosConfig';

/**
 * Hook para gerenciamento de autenticação
 * @returns {Object} Métodos e estado de autenticação
 */
export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!getJwt());
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Carregar dados do usuário
  const loadUserData = useCallback(async () => {
    if (!getJwt()) return;

    setLoading(true);
    try {
      const userData = await getUserData();
      setUser(userData);
    } catch (err) {
      console.error('Erro ao carregar dados do usuário:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Efeito para carregar dados do usuário quando o token mudar
  useEffect(() => {
    if (isAuthenticated) {
      loadUserData();
    }
  }, [isAuthenticated, loadUserData]);

  // Configurar ouvintes de eventos
  useEffect(() => {
    const handleLogin = () => {
      setIsAuthenticated(true);
      setError(null);
    };

    const handleLogout = () => {
      setIsAuthenticated(false);
      setUser(null);
    };

    const handleTokenRefreshed = () => {
      // Token foi renovado, podemos atualizar a flag de autenticação
      setIsAuthenticated(true);
      // Opcionalmente recarregar dados do usuário se necessário
    };

    // Registrar listeners para eventos de autenticação
    window.addEventListener(authEvents.login.type, handleLogin);
    window.addEventListener(authEvents.logout.type, handleLogout);
    window.addEventListener(authEvents.tokenRefreshed.type, handleTokenRefreshed);

    // Verificar autenticação inicial
    setIsAuthenticated(!!getJwt());
    if (getJwt()) {
      loadUserData();
    }

    // Limpar listeners ao desmontar
    return () => {
      window.removeEventListener(authEvents.login.type, handleLogin);
      window.removeEventListener(authEvents.logout.type, handleLogout);
      window.removeEventListener(authEvents.tokenRefreshed.type, handleTokenRefreshed);
    };
  }, [loadUserData]);

  // Método para login
  const handleLogin = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      await login(email, password);
      // O evento de login será capturado pelo listener
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Método para logout
  const handleLogout = async () => {
    setLoading(true);
    try {
      await logout();
      // O evento de logout será capturado pelo listener
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Método para forçar renovação do token
  const handleRefreshToken = async () => {
    setLoading(true);
    try {
      await refreshToken();
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Método para reset de senha
  const handleResetPassword = async (email) => {
    setLoading(true);
    setError(null);
    try {
      await resetPassword(email);
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    isAuthenticated,
    user,
    loading,
    error,
    login: handleLogin,
    logout: handleLogout,
    refreshToken: handleRefreshToken,
    resetPassword: handleResetPassword,
    loadUserData,
  };
}

export default useAuth;
